import type { BlogPost } from "~/lib/inbound-gpt/blog";
import { MARKETING_SOLUTIONS_NAV } from "~/lib/inbound-gpt/paths";
import {
  SITEGLUE_FOUNDER_PROFILE,
} from "~/lib/siteglue/blog-founder";

export type BlogArticleTag =
  | "Human-in-the-Loop™"
  | "AI Employees"
  | "Healthcare"
  | "Dental"
  | "Legal"
  | "Education"
  | "Home Services";

export type BlogArticleAuthor = {
  name: string;
  role: string;
  url?: string;
};

export type BlogRecommendedEmployee = {
  title: string;
  description: string;
  href: string;
};

export type BlogIndustryGuide = {
  label: string;
  href: string;
};

export const SITEGLUE_BLOG_AUTHOR_DEFAULT: BlogArticleAuthor = {
  name: SITEGLUE_FOUNDER_PROFILE.name,
  role: SITEGLUE_FOUNDER_PROFILE.role,
  url: `https://www.siteglue.ai${SITEGLUE_FOUNDER_PROFILE.aboutHref}`,
};

export const SITEGLUE_BLOG_INDUSTRY_GUIDES: BlogIndustryGuide[] = [
  { label: "Dental", href: "/dental" },
  { label: "Healthcare", href: "/physical-therapy" },
  { label: "Legal", href: "/legal" },
  { label: "Education", href: "/new-student-advisor" },
  { label: "Home Services", href: "/home-services" },
  { label: "Churches", href: "/ai-for-churches" },
  { label: "Government", href: "/ai-for-government" },
  { label: "Nonprofits", href: "/ai-for-nonprofits" },
];

const TAG_RULES: Array<{ tag: BlogArticleTag; patterns: RegExp[] }> = [
  {
    tag: "Human-in-the-Loop™",
    patterns: [/human[- ]in[- ]the[- ]loop/i, /\bHITL\b/, /handoff/i, /takeover/i],
  },
  {
    tag: "AI Employees",
    patterns: [/AI Employee/i, /AI Employees/i, /hire your first/i],
  },
  {
    tag: "Healthcare",
    patterns: [/healthcare/i, /patient/i, /clinic/i, /physio/i, /medical/i, /juvonno/i, /nexhealth/i],
  },
  {
    tag: "Dental",
    patterns: [/\bdental\b/i, /\bdentist\b/i, /dentrix/i],
  },
  {
    tag: "Legal",
    patterns: [/\blegal\b/i, /\blaw firm\b/i, /\battorney\b/i, /\bclio\b/i],
  },
  {
    tag: "Education",
    patterns: [/education/i, /student advisor/i, /enrollment/i, /school/i],
  },
  {
    tag: "Home Services",
    patterns: [/home services/i, /field service/i, /hvac/i, /plumbing/i],
  },
];

const EMPLOYEE_CATALOG: Array<BlogRecommendedEmployee & { tags: BlogArticleTag[] }> = [
  {
    title: "Front Desk Receptionist",
    description: "Answer calls, greet visitors, and keep humans in the loop.",
    href: "/front-desk-receptionist",
    tags: ["AI Employees", "Healthcare", "Dental", "Legal", "Home Services"],
  },
  {
    title: "Online Patient Coordinator",
    description: "Qualify inbound interest and book into your calendar or EMR.",
    href: "/online-patient-coordinator",
    tags: ["Healthcare", "Dental", "AI Employees"],
  },
  {
    title: "Scheduling Specialist",
    description: "Offer real availability and confirm appointments across channels.",
    href: "/scheduling-specialist",
    tags: ["Healthcare", "Dental", "Legal", "Home Services", "AI Employees"],
  },
  {
    title: "Online Support Specialist",
    description: "Resolve common questions, then hand off with full context.",
    href: "/online-support-agent",
    tags: ["AI Employees", "Home Services", "Legal", "Education"],
  },
  {
    title: "New Student Advisor",
    description: "Guide prospects through enrollment questions and next steps.",
    href: "/new-student-advisor",
    tags: ["Education", "AI Employees"],
  },
  {
    title: "Online Sales Representative",
    description: "Capture leads, qualify intent, and route hot opportunities.",
    href: "/online-sales-rep",
    tags: ["AI Employees", "Home Services", "Legal"],
  },
];

/** Words-per-minute reading estimate (rounded up, min 1). */
export function estimateReadingMinutes(content: string): number {
  const words = content
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/[#>*`_\[\]()|-]/g, " ")
    .split(/\s+/)
    .filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 220));
}

export function inferBlogTags(post: BlogPost): BlogArticleTag[] {
  const haystack = [post.title, post.metaDescription ?? "", post.targetKeyword ?? "", post.content]
    .join("\n")
    .slice(0, 8000);
  const tags = TAG_RULES.filter(({ patterns }) => patterns.some((re) => re.test(haystack))).map(
    (r) => r.tag,
  );
  if (!tags.includes("AI Employees") && /SiteGlue|AI agent/i.test(haystack)) {
    tags.unshift("AI Employees");
  }
  if (!tags.includes("Human-in-the-Loop™") && /human/i.test(haystack) && /loop|handoff|takeover/i.test(haystack)) {
    tags.unshift("Human-in-the-Loop™");
  }
  return Array.from(new Set(tags)).slice(0, 5);
}

export function resolveBlogAuthor(post: BlogPost): BlogArticleAuthor {
  const byline = post.content.match(/\*\*By\s+([^,*]+?)(?:,\s*([^*]+))?\*\*/i);
  if (byline?.[1]) {
    return {
      name: byline[1].trim(),
      role: (byline[2] ?? SITEGLUE_BLOG_AUTHOR_DEFAULT.role).trim(),
      url: SITEGLUE_BLOG_AUTHOR_DEFAULT.url,
    };
  }
  if (/Jim Wanek/i.test(post.content) || /founder of SiteGlue/i.test(post.content)) {
    return SITEGLUE_BLOG_AUTHOR_DEFAULT;
  }
  return {
    name: "SiteGlue",
    role: "AI Employee Platform",
    url: "https://www.siteglue.ai",
  };
}

/** Strip duplicate H1 + leading byline from markdown body for template rendering. */
export function stripBlogArticleChrome(content: string): string {
  return content
    .replace(/^#\s+.+\n+/, "")
    .replace(/^\*\*By\s+[^*]+\*\*\s*\n+/, "")
    .replace(/^---\s*\n+/, "")
    .trim();
}

export function getRelatedBlogPosts(post: BlogPost, all: BlogPost[], limit = 4): BlogPost[] {
  const tags = new Set(inferBlogTags(post));
  const scored = all
    .filter((p) => p.id !== post.id && p.slug)
    .map((candidate) => {
      const candidateTags = inferBlogTags(candidate);
      const overlap = candidateTags.filter((t) => tags.has(t)).length;
      const keywordHit =
        post.targetKeyword &&
        candidate.targetKeyword &&
        post.targetKeyword.split(",")[0]?.trim() &&
        candidate.targetKeyword.toLowerCase().includes(
          post.targetKeyword.split(",")[0]!.trim().toLowerCase().slice(0, 12),
        )
          ? 1
          : 0;
      return { candidate, score: overlap * 2 + keywordHit };
    })
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return new Date(b.candidate.createdAt).getTime() - new Date(a.candidate.createdAt).getTime();
    });

  const related = scored.filter((s) => s.score > 0).map((s) => s.candidate);
  if (related.length >= limit) return related.slice(0, limit);
  const recent = all
    .filter((p) => p.id !== post.id && p.slug && !related.some((r) => r.id === p.id))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  return [...related, ...recent].slice(0, limit);
}

export function getRecommendedEmployeesForPost(post: BlogPost, limit = 3): BlogRecommendedEmployee[] {
  const tags = inferBlogTags(post);
  const ranked = EMPLOYEE_CATALOG.map((emp) => ({
    emp,
    score: emp.tags.filter((t) => tags.includes(t)).length,
  })).sort((a, b) => b.score - a.score);

  const picked = ranked.filter((r) => r.score > 0).map((r) => r.emp);
  const fallback = MARKETING_SOLUTIONS_NAV.slice(0, limit).map((item) => ({
    title: item.label,
    description: "Explore this AI Employee role landing page.",
    href: item.href,
  }));
  const merged = [...picked, ...EMPLOYEE_CATALOG.filter((e) => !picked.includes(e))];
  return (merged.length ? merged : fallback).slice(0, limit).map(({ title, description, href }) => ({
    title,
    description,
    href,
  }));
}

export function getPopularBlogPosts(all: BlogPost[], excludeId?: number, limit = 4): BlogPost[] {
  return all
    .filter((p) => p.id !== excludeId && p.slug)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, limit);
}

export function formatBlogDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}
